<template>
  <div
    v-if="rows.length"
    class="row signature-list"
    :class="{ 'signature-list--compact': compact }"
  >
    <div class="col-xs-12 signature-list__content">
      <h4 class="text-center">Signatures</h4>
      <div
        v-for="(row, index) in rows"
        :key="rowKey(row, index)"
        class="well signature-list__item"
        :class="{ 'signature-list__item--awaiting': !isSigned(row) }"
      >
        <p class="signature-list__meta">
          <strong>{{ roleLabel(row) }}</strong>
          <span
            class="signature-list__status"
            :class="
              isSigned(row)
                ? 'signature-list__status--signed'
                : 'signature-list__status--awaiting'
            "
          >
            {{ isSigned(row) ? "Signed" : "Awaiting signature" }}
          </span>
        </p>

        <template v-if="isSigned(row)">
          <p v-if="row.signed_at" class="signature-list__when text-muted">
            {{ formatSignedAt(row.signed_at) }}
          </p>
          <img
            v-if="row.signature_data"
            :src="row.signature_data"
            :alt="roleLabel(row) + ' signature'"
            class="signature-list__image"
          />
        </template>

        <p v-else class="signature-list__when text-muted">
          {{ awaitingDetail(row) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
const ROLE_LABELS = {
  student: "Student",
  guardian: "Guardian",
  instructor: "Instructor",
  school: "School",
  organisation: "Organisation",
};

const SIGNED = "signed";

export default {
  name: "DsSignatureList",
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    /**
     * The lesson's signature roster from the API: one row per signature the
     * lesson carries or is expected to carry, marked signed or awaiting.
     * Rows the viewer may not see are filtered out server-side.
     */
    roster: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    rows() {
      return this.roster || [];
    },
  },
  methods: {
    isSigned(row) {
      return row.status === SIGNED;
    },

    /**
     * "Guardian", or "Guardian (Louise Belcher)" on an observation lesson,
     * where several students share one car and the role alone is ambiguous.
     * The name is the passenger the signature belongs to, never the signer --
     * a guardian signature carries no account of its own.
     */
    roleLabel(row) {
      const role = row.signee_type
        ? ROLE_LABELS[row.signee_type] ||
          row.signee_type.charAt(0).toUpperCase() + row.signee_type.slice(1)
        : "Signature";

      return row.student_name ? `${role} (${row.student_name})` : role;
    },

    awaitingDetail(row) {
      if (row.invitation_expired) {
        return "The signing link sent to this guardian has expired. Send a new one to collect the signature.";
      }
      if (row.invitation_sent_at) {
        return `Signing link emailed ${this.formatSignedAt(
          row.invitation_sent_at
        )} — not yet signed.`;
      }
      return "Not yet signed.";
    },

    rowKey(row, index) {
      return `${row.signee_type}:${row.student_id || "lesson"}:${index}`;
    },

    formatSignedAt(signedAt) {
      const date = new Date(signedAt);
      if (isNaN(date.getTime())) {
        return "";
      }
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.signature-list__item {
  margin-bottom: 1em;
}

.signature-list__item--awaiting {
  border-left: 3px solid #e0a800;
}

.signature-list__meta {
  margin-bottom: 0.35em;
}

.signature-list__status {
  float: right;
  font-size: 0.75em;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.2em 0.6em;
  border-radius: 3px;
}

.signature-list__status--signed {
  color: #0b5d3b;
  background-color: #d6f0e2;
}

.signature-list__status--awaiting {
  color: #7a5300;
  background-color: #fdf0cf;
}

.signature-list__when {
  margin-bottom: 0.5em;
  font-size: 0.9em;
}

.signature-list__image {
  max-width: 100%;
  height: auto;
  border: 1px solid #ccc;
  background-color: #fff;
}

.signature-list--compact {
  margin: 0;
}

.signature-list--compact .signature-list__content {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0;
}

.signature-list--compact h4 {
  width: 100%;
  margin: 0;
  text-align: left;
  font-size: 14px;
}

.signature-list--compact .signature-list__item {
  flex: 1 1 220px;
  min-width: 0;
  padding: 12px;
  margin: 0;
  break-inside: avoid;
}

.signature-list--compact .signature-list__meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 6px;
}

.signature-list--compact .signature-list__image {
  display: block;
  width: 100%;
  height: 80px;
  object-fit: contain;
}
</style>
